import { useState, useEffect, useRef, Suspense, lazy } from 'react'
import { useLocation } from 'react-router-dom'
import { Beacon, Loader } from '../ui'
import FaqPanel from '../pages/FaqPanel'
import Tickets from '../pages/Tickets'
import Kritik from '../pages/Kritik'
import { Operators, Reports, Audit, Categories, Templates, Settings } from '../pages/OtherPages'
import {
  HiXMark, HiQuestionMarkCircle, HiInbox, HiSquares2X2,
  HiMap, HiPresentationChartLine, HiClipboardDocumentList,
  HiUsers, HiTag, HiDocumentText, HiCog6Tooth,
} from 'react-icons/hi2'
import { useStore } from '../store'

const Dashboard = lazy(() => import('../pages/Dashboard'))
const MapPage   = lazy(() => import('../pages/MapPage'))

// ─────────────────────────────────────────────────────────
//  SPLIT (2 panelli) TIZIM
//  Chap menyudan istalgan sahifani o'ng tomonga sudrab
//  tashlang — ekran ikkiga bo'linadi. O'rtadagi chiziqni
//  torting — kenglik o'zgaradi. ✕ — yopish.
// ─────────────────────────────────────────────────────────
export const SPLIT_PAGES = {
  faq:        { t: 'Resurslar yordamchi', I: HiQuestionMarkCircle, C: FaqPanel },
  tickets:    { t: 'Murojaatlar',   I: HiInbox,                 C: Tickets },
  dashboard:  { t: 'Dashboard',     I: HiSquares2X2,            C: Dashboard },
  kritik:     { t: 'Kritik',        I: Beacon,                  C: Kritik },
  map:        { t: 'Xarita',        I: HiMap,                   C: MapPage },
  reports:    { t: 'Hisobotlar',    I: HiPresentationChartLine, C: Reports },
  audit:      { t: 'Audit jurnali', I: HiClipboardDocumentList, C: Audit },
  operators:  { t: 'Operatorlar',   I: HiUsers,                 C: Operators },
  categories: { t: 'Kategoriyalar', I: HiTag,                   C: Categories },
  templates:  { t: 'Shablonlar',    I: HiDocumentText,          C: Templates },
  settings:   { t: 'Sozlamalar',    I: HiCog6Tooth,             C: Settings },
}

export default function SplitLayout({ children }) {
  const { splitPage, setSplitPage } = useStore()
  const location = useLocation()

  useEffect(() => {
    if (!location.pathname.startsWith('/ticket/')) {
      setSplitPage(null)
    }
  }, [location.pathname, setSplitPage])

  const [ratio, setRatio] = useState(() => {
    const r = parseFloat(localStorage.getItem('split_ratio') ?? '58')
    return r >= 25 && r <= 75 ? r : 58
  })
  const [dragOver, setDragOver] = useState(false)
  const wrapRef  = useRef(null)
  const resizing = useRef(false)

  useEffect(() => {
    try {
      localStorage.setItem('split_ratio', String(Math.round(ratio)))
    } catch {}
  }, [ratio])

  // O'rta chiziqni sudrab kenglik o'zgartirish
  useEffect(() => {
    const mv = e => {
      if (!resizing.current || !wrapRef.current) return
      const r = wrapRef.current.getBoundingClientRect()
      const x = e.touches ? e.touches[0].clientX : e.clientX
      setRatio(Math.min(75, Math.max(25, ((x - r.left) / r.width) * 100)))
    }
    const up = () => { resizing.current = false; document.body.style.userSelect = '' }
    window.addEventListener('pointermove', mv)
    window.addEventListener('pointerup', up)
    return () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up) }
  }, [])

  // Drag-and-drop jarayoni yakunlanganda (cancel bo'lsa ham) obvodkani o'chirish
  useEffect(() => {
    const handleDragEnd = () => {
      setDragOver(false)
    }
    window.addEventListener('dragend', handleDragEnd)
    return () => window.removeEventListener('dragend', handleDragEnd)
  }, [])

  const hasPageDrag = e =>
    [...(e.dataTransfer?.types || [])].includes('application/x-page')

  const onDragOver = e => { if (hasPageDrag(e)) { e.preventDefault(); setDragOver(true) } }
  const onDrop = e => {
    setDragOver(false)
    const id = e.dataTransfer.getData('application/x-page')
    if (SPLIT_PAGES[id]) { e.preventDefault(); setSplitPage(id) }
  }

  const P = splitPage ? SPLIT_PAGES[splitPage] : null

  return (
    /* `is-split` — panel ochiqligini CSS ga bildiradi: faqat o'shanda ikki
       yarim alohida suriladi. Panel yopiq bo'lsa sahifa bitta joyda suriladi
       (ikkita sursurgich va o'rinsiz gorizontal chiziq bo'lmasligi uchun). */
    <div ref={wrapRef} className={`split-wrap ${P ? 'is-split' : ''}`}
         onDragOver={onDragOver}
         onDragLeave={e => { if (e.target === e.currentTarget) setDragOver(false) }}
         onDrop={onDrop}>

      <div className="split-main" style={{ width: P ? `${ratio}%` : '100%' }}>
        {children}
      </div>

      {P && (
        <>
          <div className="split-divider" title="Torting — kenglik o'zgaradi"
               onPointerDown={e => { resizing.current = true; document.body.style.userSelect = 'none'; e.preventDefault() }} />
          <div className="split-pane" style={{ flex: 1 }}>
            <div className="split-pane-hdr">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                <P.I style={{ fontSize: 16, color: 'var(--ui-text-1)', flexShrink: 0 }} />
                <select
                  value={splitPage}
                  onChange={(e) => setSplitPage(e.target.value || null)}
                  className="split-select-dropdown"
                  title="Panel sahifasini tanlang"
                >
                  {Object.entries(SPLIT_PAGES).map(([id, p]) => (
                    <option key={id} value={id}>
                      {p.t}
                    </option>
                  ))}
                </select>
              </div>
              <button className="split-x" title="Panelni yopish" onClick={() => setSplitPage(null)}><HiXMark /></button>
            </div>
            <div className="split-pane-body">
              <Suspense fallback={<div style={{ padding: 30, textAlign: 'center' }}><Loader state="working" size="sm" /></div>}>
                <P.C key={splitPage} embedded />
              </Suspense>
            </div>
          </div>
        </>
      )}

      <div className={`split-dropzone ${dragOver ? 'active' : ''}`} style={{ left: P ? `${ratio}%` : '50%' }}>
        <div className="split-dropzone-inner">
          {P ? 'Panelni almashtirish uchun tashlang' : "Bu yerga tashlang — ekran 2 ga bo'linadi"}
        </div>
      </div>
    </div>
  )
}

